import { EmpathyAdapter, EmpathyAdapterBuilder, EmpathyAdapterConfig, FacetConfig, FeatureConfig } from '@empathy/search-adapter';
import {
  DeepPartial,
  DEPENDENCIES,
  Dictionary,
  DynamicConfig,
  EmpathyXConfig,
  FacetConfig as EmpathyXFacetConfig,
  GETTERS_KEYS,
  RootStoreState,
  UserService
} from '@empathybroker/ebx-gluon';
import { Container } from 'inversify';
import { Store } from 'vuex';

export interface UseEmpathyAdapterOptions {
  configureAdapter?: (builder: Omit<EmpathyAdapterBuilder, 'build'>) => void;
}

export function useEmpathyAdapter({ configureAdapter = noop }: UseEmpathyAdapterOptions = {}) {
  return ({ container, store }) => {
    const builder = new EmpathyAdapterBuilder();
    configureAdapter(builder);
    const config: EmpathyXConfig = store.getters[GETTERS_KEYS.config.config];
    const defaultFacetsMappingConfig = config.facets.default.facet.mapping;
    const namedFacetsConfig = getFacetsConfig(config.facets.facets);
    const facetsConfig = {
      mappings: {
        facets: {
          default: defaultFacetsMappingConfig,
          named: namedFacetsConfig
        }
      }
    };
    const adapter = builder
      .withConfiguration(facetsConfig)
      .build();

    createDynamicConfigWatchers(adapter, container, store);

    return adapter;
  };
}

function getFacetsConfig(facetsConfig: Dictionary<DeepPartial<EmpathyXFacetConfig>> = {}) {
  return Object.entries(facetsConfig)
    .reduce((namedFacets, [facetName, { mapping = {} }]) => {
      namedFacets[facetName] = mapping;
      return namedFacets;
    }, {} as Record<string, DeepPartial<FacetConfig>>);
}

function createDynamicConfigWatchers(adapter: EmpathyAdapter, container: Container, vuexStore: Store<RootStoreState>) {
  vuexStore.watch(state => state.config.dynamic, syncDynamicConfig(adapter, container), { immediate: true, deep: true });
}

function syncDynamicConfig(adapter: EmpathyAdapter, container: Container) {
  return ({ scope, consent, searchLang: lang, env, instance, store, warehouse, catalog: catalogue, endpoints }: DynamicConfig) => {
    adapter.setConfig({
      instance,
      requestParams: { scope, lang, store, warehouse, catalogue, ...getUserRequestParams(container, consent) },
      env: getEnvironment(env),
      features: getFeaturesConfig(endpoints || {})
    });
  };
}

function getEnvironment(env?: string): EmpathyAdapterConfig['env'] {
  if (env && !['live', 'staging', 'test'].includes(env)) {
    console.warn(`The environment "${ env }" is not recognized.`);
  }
  return env || 'live' as any;
}

function getFeaturesConfig(endpoints: Dictionary<string>) {
  return Object.entries(endpoints).reduce((adapterEndpoints, [key, endpoint]) => {
    adapterEndpoints[key] = { endpoint };
    return adapterEndpoints;
  }, {} as Record<string, DeepPartial<FeatureConfig<any>>>);
}

function getUserRequestParams(container: Container, consent: boolean): Dictionary<string> {
  const userService = container.get<UserService>(DEPENDENCIES.UserService);
  if (consent) {
    const { user, session, userType } = userService.getUserInfo();
    return {
      user,
      session,
      user_type: userType
    };
  } else {
    userService.removeUserInfo();
    return {
      user: undefined,
      session: undefined,
      user_type: undefined
    };
  }
}

function noop() {}
